import React from 'react';

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  theme?: 'default' | 'admin';
}

const TableContext = React.createContext<{ theme: 'default' | 'admin' }>({ theme: 'default' });

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className = '', theme = 'default', ...props }, ref) => (
    <TableContext.Provider value={{ theme }}>
    <div 
      className={theme === 'admin' ? "w-full overflow-x-auto border" : "w-full overflow-hidden border rounded-2xl shadow-sm my-6"}
      style={{
        backgroundColor: 'var(--admin-card-bg, #ffffff)',
        borderColor: 'var(--admin-card-border, #e2e8f0)',
        borderRadius: theme === 'admin' ? '0' : undefined,
      }}
    >
      <table
        ref={ref}
        className={`w-full border-collapse text-left text-sm ${className}`}
        style={{ 
          color: 'var(--admin-card-text, #334155)',
          fontFamily: theme === 'admin' ? "'Inter', sans-serif" : undefined,
          fontSize: theme === 'admin' ? '12px' : undefined
        }}
        {...props}
      />
    </div>
    </TableContext.Provider>
  )
);
Table.displayName = 'Table';

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className = '', ...props }, ref) => {
  const { theme } = React.useContext(TableContext);
  return (
    <thead
      ref={ref}
      className={className}
      style={{
        backgroundColor: theme === 'admin' ? 'var(--admin-table-header-bg, rgba(255, 255, 255, 0.02))' : 'var(--admin-table-header-bg, #f8fafc)',
        borderBottom: '1px solid var(--admin-card-border, #e2e8f0)',
      }}
      {...props}
    />
  );
});
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className = '', ...props }, ref) => (
  <tbody
    ref={ref}
    className={className}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className = '', style, ...props }, ref) => {
  const [hovered, setHovered] = React.useState(false);
  const { theme } = React.useContext(TableContext);
  
  // Custom hover logic based on theme
  const getHoverBg = () => {
    if (!hovered) return 'transparent';
    return theme === 'admin' ? 'rgba(255, 255, 255, 0.02)' : 'var(--admin-sidebar-link-inactive-hover-bg, #f8fafc)';
  };

  return (
    <tr
      ref={ref}
      className={`transition-colors duration-150 ${className}`}
      style={{
        borderBottom: '1px solid var(--admin-card-border, #f1f5f9)',
        cursor: props.onClick ? 'pointer' : 'default',
        backgroundColor: getHoverBg(),
        ...style
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...props}
    />
  );
});
TableRow.displayName = 'TableRow';

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className = '', style, ...props }, ref) => {
  const { theme } = React.useContext(TableContext);
  return (
    <th
      ref={ref}
      className={theme === 'admin' ? `font-bold tracking-wider ${className}` : `text-[11px] font-bold uppercase tracking-wider ${className}`}
      style={{ 
        padding: theme === 'admin' ? '12px 16px' : '18px 24px', 
        color: theme === 'admin' ? 'var(--admin-card-subtext, #475569)' : 'var(--admin-card-text, #475569)',
        textTransform: 'uppercase',
        fontSize: theme === 'admin' ? '10px' : undefined,
        letterSpacing: theme === 'admin' ? '0.05em' : undefined,
        opacity: theme === 'admin' ? undefined : 0.8,
        ...style 
      }}
      {...props}
    />
  );
});
TableHead.displayName = 'TableHead';

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className = '', style, ...props }, ref) => {
  const { theme } = React.useContext(TableContext);
  return (
    <td
      ref={ref}
      className={`align-middle ${theme === 'admin' ? '' : 'font-medium'} ${className}`}
      style={{ 
        padding: theme === 'admin' ? '14px 16px' : '16px 24px', 
        color: theme === 'admin' ? 'var(--admin-card-text, #4b5563)' : 'var(--admin-card-subtext, #4b5563)',
        ...style 
      }}
      {...props}
    />
  );
});
TableCell.displayName = 'TableCell';
